// Firebase configuration and initialization with environment variables
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';

const cleanEnv = (str: string | undefined) => str?.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/^["']+|["']+$/g, '');

// Environment variables with fallbacks for development
const firebaseConfig = {
  apiKey: cleanEnv(process.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: cleanEnv(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  databaseURL: cleanEnv(process.env.REACT_APP_FIREBASE_DATABASE_URL),
  projectId: cleanEnv(process.env.REACT_APP_FIREBASE_PROJECT_ID),
  storageBucket: cleanEnv(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: cleanEnv(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: cleanEnv(process.env.REACT_APP_FIREBASE_APP_ID),
  measurementId: cleanEnv(process.env.REACT_APP_FIREBASE_MEASUREMENT_ID)
};

// VAPID key from environment variables
const VAPID_KEY = cleanEnv(process.env.REACT_APP_FIREBASE_VAPID_KEY);

// Initialize Firebase app
let app: FirebaseApp | undefined;
let messaging: Messaging | null = null;

// Initialize Firebase only when needed and supported
const initializeFirebaseApp = async (): Promise<FirebaseApp> => {
  app ??= initializeApp(firebaseConfig);
  return app;
};

// Lazy initialize messaging only when needed
const initializeMessaging = async (): Promise<Messaging | null> => {
  if (!messaging && typeof globalThis.window !== 'undefined' && 'serviceWorker' in navigator) {
    // Check if messaging is supported
    const supported = await isSupported();
    if (!supported) {
      return null;
    }

    app ??= await initializeFirebaseApp();

    try {
      // Ensure service worker is ready before initializing messaging (with timeout)
      if ('serviceWorker' in navigator) {
        const serviceWorkerReady = Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker ready timeout')), 5000))
        ]);
        
        await serviceWorkerReady.catch((error) => {
          console.warn('Service worker not ready within timeout, continuing anyway:', error);
        });
      }
      
      messaging = getMessaging(app);
      return messaging;
    } catch (error: any) {
      // Reset messaging to null so it can be retried
      messaging = null;
      throw new Error(`Firebase messaging initialization failed: ${error.message}`);
    }
  }
  return messaging;
};

// Browser instance ID management
export function getBrowserInstanceId(): string {
  let browserInstanceId = localStorage.getItem('fcm_browser_instance_id');
  
  // Use nullish coalescing to generate UUID only if not already stored
  browserInstanceId ??= (() => {
    let newId: string;
    // Fallback UUID generation for older browsers
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      newId = crypto.randomUUID();
    } else {
      // Fallback UUID v4 generation
      newId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.trunc(Math.random() * 16);
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
    localStorage.setItem('fcm_browser_instance_id', newId);
    return newId;
  })();
  
  return browserInstanceId;
}

// Check if browser supports notifications
export function isNotificationSupported(): boolean {
  // Check if we're in a browser environment with notification support
  if (typeof globalThis.window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check for required APIs
  const hasNotificationAPI = 'Notification' in globalThis.window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in globalThis.window;

  // Check if we're in a secure context (HTTPS, localhost, or 127.0.0.1)
  const isSecureContext = globalThis.window.isSecureContext;

  return hasNotificationAPI && hasServiceWorker && hasPushManager && isSecureContext;
}

// Request notification permission
export async function requestNotificationPermission(): Promise<{ success: boolean; error?: string; permission?: string }> {
  if (!isNotificationSupported()) {
    return { success: false, error: 'Notifications not supported' };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      return { success: true, permission };
    } else {
      return { success: false, error: 'Permission denied', permission };
    }
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// Clean up existing service worker registrations to prevent duplicates
export async function cleanupServiceWorkerRegistrations(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    // Get all registrations
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    // Filter for Firebase-related registrations with scope '/'
    const duplicateRegistrations = registrations.filter(reg =>
      reg.scope.includes('://') &&
      reg.scope.endsWith('/') &&
      !reg.scope.includes('firebase-cloud-messaging-push-scope')
    );
    
    // Unregister duplicate registrations
    for (const registration of duplicateRegistrations) {
      await registration.unregister();
    }
  } catch (error) {
    console.warn('Error cleaning up service worker registrations:', error);
  }
}

// Get FCM token
export async function getFCMToken(): Promise<string> {
  if (!isNotificationSupported()) {
    throw new Error('FCM not supported');
  }

  try {
    // Initialize messaging
    const messagingInstance = await initializeMessaging();
    if (!messagingInstance) {
      throw new Error('Firebase messaging not available');
    }

    // Clean up any duplicate service worker registrations
    await cleanupServiceWorkerRegistrations();

    // Get FCM token (Firebase will handle service worker registration automatically)
    const token = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
    });

    if (token) {
      return token;
    } else {
      throw new Error('No registration token available');
    }
  } catch (error: any) {
    throw new Error(`FCM token retrieval failed: ${error.message}`);
  }
}

// Message handler type
type MessageHandler = (payload: any) => void;

// Setup foreground message handling
export function setupForegroundMessageHandling(callback?: MessageHandler): (() => void) {
  if (!isNotificationSupported()) {
    return () => { };
  }

  try {
    // Initialize messaging with await
    initializeMessaging()
      .then((messagingInstance) => {
        if (!messagingInstance) {
          return;
        }

        const unsubscribe = onMessage(messagingInstance, (payload) => {
          // Call the provided callback with the message payload
          if (callback && typeof callback === 'function') {
            callback(payload);
          }

          // Show browser notification if the app is in focus
          if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
            showBrowserNotification(payload);
          }
        });

        // Store the unsubscribe function globally for cleanup
        globalMessageUnsubscribe = unsubscribe;

        return unsubscribe;
      })
      .catch((error) => {
        console.error('Failed to initialize messaging for foreground handling:', error);
      });

    // Return empty unsubscribe function as fallback
    return () => { };
  } catch (error) {
    return () => { };
  }
}

// Show browser notification
function showBrowserNotification(payload: any): void {
  if (typeof globalThis.window === 'undefined' || typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }

  const notificationTitle = payload.notification?.title || 'New Notification';
  const notificationOptions: NotificationOptions = {
    body: payload.notification?.body || 'You have a new message',
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: payload.data?.type || 'default',
    data: payload.data || {},
    requireInteraction: false,
    silent: false
  };

  try {
    new Notification(notificationTitle, notificationOptions);
  } catch (error) {
    // Handle any errors that occur when creating the notification
  }
}

// Get current notification permission status
export function getNotificationPermission(): string {
  // Check if notifications are supported first
  if (!isNotificationSupported()) {
    return 'unsupported';
  }

  // Get actual browser permission
  if (typeof Notification !== 'undefined') {
    return Notification.permission;
  }

  return 'unsupported';
}

// Check if FCM token exists in localStorage
export function hasStoredFCMToken(): boolean {
  return Boolean(localStorage.getItem('fcm_token'));
}

// Store FCM token in localStorage
export function storeFCMToken(token: string): void {
  if (token) {
    localStorage.setItem('fcm_token', token);
  }
}

// Get stored FCM token
export function getStoredFCMToken(): string | null {
  return localStorage.getItem('fcm_token');
}

// Clear stored FCM token
export function clearStoredFCMToken(): void {
  localStorage.removeItem('fcm_token');
}

// Global unsubscribe function to manage message handlers
let globalMessageUnsubscribe: (() => void) | null = null;

// Initialize FCM result interface
interface FCMInitResult {
  success: boolean;
  error?: string;
  permission?: string;
  unsubscribe?: () => void;
  browserInstanceId?: string;
}

// Completely cleanup FCM - stop all message handlers and clear tokens
export async function cleanupFCM(): Promise<void> {
  try {
    // Unsubscribe from message handlers
    if (globalMessageUnsubscribe) {
      globalMessageUnsubscribe();
      globalMessageUnsubscribe = null;
    }

    // Clear stored FCM token
    clearStoredFCMToken();

    // Reset messaging instance
    messaging = null;
  } catch (error) {
    console.error('Error during FCM cleanup:', error);
  }
}

// Check if FCM is active (has message handlers)
export function isFCMActive(): boolean {
  return globalMessageUnsubscribe !== null;
}

// Initialize FCM with error handling
export async function initializeFCM(messageCallback?: MessageHandler): Promise<FCMInitResult> {
  try {
    // Check if notifications are supported
    if (!isNotificationSupported()) {
      return { success: false, error: 'Notifications not supported' };
    }

    // Clean up duplicate service worker registrations first
    await cleanupServiceWorkerRegistrations();

    // Wait a moment for cleanup to complete
    await new Promise(resolve => setTimeout(resolve, 100));

    // Check current permission status
    const permission = getNotificationPermission();

    if (permission === 'denied') {
      return { success: false, error: 'Notifications are blocked' };
    }

    // Ensure messaging is properly initialized before setting up handlers
    const messagingInstance = await initializeMessaging();
    if (!messagingInstance) {
      return { success: false, error: 'Failed to initialize messaging' };
    }

    // Setup foreground message handling
    const unsubscribe = setupForegroundMessageHandling(messageCallback);

    const browserInstanceId = getBrowserInstanceId();
    
    return {
      success: true,
      permission,
      unsubscribe,
      browserInstanceId
    };

  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// Export types for TypeScript consumers
export type { MessageHandler, FCMInitResult };

// Default export for backward compatibility
export default {
  getBrowserInstanceId,
  isNotificationSupported,
  requestNotificationPermission,
  getFCMToken,
  setupForegroundMessageHandling,
  getNotificationPermission,
  hasStoredFCMToken,
  storeFCMToken,
  getStoredFCMToken,
  clearStoredFCMToken,
  initializeFCM
};